import React, { useState, useMemo, useEffect, useCallback } from 'react'
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import {
    CatalogFilterBar,
    CatalogFilterDrawer,
    CatalogSelectField,
    FilterChipItem,
    PropertyTagFilterField,
    getPropertyTagChip,
    uniqueNamedOptions,
} from '../../../ContractManager/CatalogFilterChrome'
import BusinessIcon from '@material-ui/icons/Business'
import SendIcon from '@material-ui/icons/Send'
import RefreshIcon from '@material-ui/icons/Refresh'
import VisibilityIcon from '@material-ui/icons/Visibility'
import DeleteIcon from '@material-ui/icons/Delete'
import DescriptionIcon from '@material-ui/icons/Description'
import { useOrgSampleW9s, SampleW9 } from '../../../../hooks/useOrgSampleW9s'
import { useUserTags } from '../../../../contexts/UserTagsContext'
import { DeleteConfirmDialog } from '../../../ContractManager/DeleteConfirmDialog'
import { DocumentPreviewModal } from '../../../ContractManager/DocumentPreviewModal'
import { formatDate } from '../../../ContractManager/utils'
import { DropFiles } from '../../../../components/DropFiles/DropFiles'
import { Company } from '../../../../models/Company/types'
import { SelectPropertiesUploadDialog } from './SelectPropertiesUploadDialog'
import { SendSampleToVendorsDialog } from '../../../ContractManager/SendSampleToVendorsDialog'
import { useOrganizationManagement } from '../../../../hooks/useOrganizationManagement'

const allowedFileTypes = {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
        '.docx',
    ],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
}

function propertyIdsOf(item: { apartment: number; apartment_ids?: number[] }) {
    return item.apartment_ids && item.apartment_ids.length > 0
        ? item.apartment_ids
        : [item.apartment]
}

function propertyLabelOf(item: {
    apartment_name: string
    apartment_names?: string[]
}) {
    if (item.apartment_names && item.apartment_names.length > 0) {
        return item.apartment_names.join(', ')
    }
    return item.apartment_name
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    searchField: {
        minWidth: 300,
    },
    tableWrapper: {
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
    },
    tableContainer: {
        maxHeight: 'calc(100vh - 550px)',
    },
    headerRow: {
        backgroundColor: theme.palette.grey[100],
    },
    headerCell: {
        fontWeight: 600,
        color: theme.palette.text.secondary,
        fontSize: '13px',
    },
    row: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    nameCell: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    documentIcon: {
        height: 32,
        width: 32,
        borderRadius: theme.spacing(0.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.primary.light + '20',
        color: theme.palette.primary.main,
    },
    analyzingContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    analyzingText: {
        fontStyle: 'italic',
        fontSize: '13px',
    },
    summaryCell: {
        color: theme.palette.text.secondary,
        fontSize: '13px',
        minWidth: 200,
        maxWidth: 400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    actionsCell: {
        textAlign: 'right',
        whiteSpace: 'nowrap',
        width: '1%',
        minWidth: 100,
    },
    propertyCell: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    tagChip: {
        height: 20,
        fontSize: '11px',
        fontWeight: 500,
    },
    emptyRow: {
        textAlign: 'center',
        color: theme.palette.text.secondary,
        padding: theme.spacing(4),
    },
}))

interface OrganizationSampleW9sProps {
    organization: number | null
    selectedTagId: number | null
    onSelectedTagIdChange: (value: number | null) => void
    propertyList: Company[]
}

export const OrganizationSampleW9s: React.FC<OrganizationSampleW9sProps> = ({
    organization,
    selectedTagId,
    onSelectedTagIdChange,
    propertyList,
}) => {
    const classes = useStyles()
    const { userTags } = useUserTags()

    const {
        sampleW9s,
        isLoadingSampleW9s,
        deleteSampleW9,
        updateSampleW9,
        uploadSampleW9s,
        processSampleW9,
        processingIds,
        sendToVendors,
        isDeleting,
        isUpdating,
        isUploading,
        isSending,
    } = useOrgSampleW9s({ organization })

    const {
        vendorList,
        loadingState,
        getOrganizationVendors,
    } = useOrganizationManagement()

    useEffect(() => {
        if (organization) {
            getOrganizationVendors(organization)
        }
    }, [organization])

    // Search state
    const [searchText, setSearchText] = useState('')
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
    const [propertyFilter, setPropertyFilter] = useState<'all' | number>('all')
    const [reviewFilter, setReviewFilter] = useState<
        'all' | 'analyzed' | 'pending'
    >('all')

    // Pagination state
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    // Delete state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [sampleToDelete, setSampleToDelete] = useState<SampleW9 | null>(null)
    const [sampleToSend, setSampleToSend] = useState<SampleW9 | null>(null)
    const sendVendors = useMemo(() => {
        if (!sampleToSend) return []
        const propertyIds = new Set(propertyIdsOf(sampleToSend))
        return vendorList.filter((vendor) =>
            vendor.properties?.some((property) => propertyIds.has(property.id)),
        )
    }, [sampleToSend, vendorList])
    const [propertiesTarget, setPropertiesTarget] = useState<SampleW9 | null>(
        null,
    )
    const [pendingFiles, setPendingFiles] = useState<File[]>([])
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false)

    useEffect(() => {
        setPage(0)
    }, [selectedTagId, propertyFilter, reviewFilter])

    // Get all tags for a property
    const getTagsForProperty = useCallback(
        (apartmentId: number) => {
            return userTags.filter((tag) =>
                tag.apartments.includes(apartmentId),
            )
        },
        [userTags],
    )

    const propertyOptions = useMemo(() => {
        const names = new Map<number, string>()
        sampleW9s.forEach((w9) => {
            const ids = propertyIdsOf(w9)
            const labels =
                w9.apartment_names && w9.apartment_names.length > 0
                    ? w9.apartment_names
                    : [w9.apartment_name]
            ids.forEach((id, index) => {
                names.set(id, labels[index] || `Property ${id}`)
            })
        })
        return uniqueNamedOptions(
            Array.from(names.entries()).map(([id, name]) => ({ id, name })),
        )
    }, [sampleW9s])

    const filteredW9s = useMemo(() => {
        let filtered = sampleW9s

        if (selectedTagId !== null) {
            const selectedTag = userTags.find((tag) => tag.id === selectedTagId)
            if (selectedTag) {
                filtered = filtered.filter((w9) =>
                    propertyIdsOf(w9).some((id) =>
                        selectedTag.apartments.includes(id),
                    ),
                )
            }
        }
        if (propertyFilter !== 'all') {
            filtered = filtered.filter((w9) =>
                propertyIdsOf(w9).includes(propertyFilter),
            )
        }
        if (reviewFilter === 'analyzed') {
            filtered = filtered.filter((w9) => Boolean(w9.ai_summary))
        } else if (reviewFilter === 'pending') {
            filtered = filtered.filter((w9) => !w9.ai_summary)
        }

        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase()
            filtered = filtered.filter((w9) => {
                const nameMatch = w9.name.toLowerCase().includes(searchLower)
                const summaryMatch = w9.ai_summary
                    ?.toLowerCase()
                    .includes(searchLower)
                const apartmentMatch = propertyLabelOf(w9)
                    .toLowerCase()
                    .includes(searchLower)
                return nameMatch || summaryMatch || apartmentMatch
            })
        }

        return filtered
    }, [
        sampleW9s,
        searchText,
        selectedTagId,
        userTags,
        propertyFilter,
        reviewFilter,
    ])

    const clearDrawerFilters = () => {
        setPropertyFilter('all')
        setReviewFilter('all')
        onSelectedTagIdChange(null)
        setPage(0)
    }

    // Paginate filtered W-9s
    const paginatedW9s = useMemo(() => {
        const start = page * rowsPerPage
        const end = start + rowsPerPage
        return filteredW9s.slice(start, end)
    }, [filteredW9s, page, rowsPerPage])

    const filterChips = useMemo(() => {
        const chips: FilterChipItem[] = []
        const tagChip = getPropertyTagChip(selectedTagId, userTags, () =>
            onSelectedTagIdChange(null),
        )
        if (tagChip) chips.push(tagChip)
        if (propertyFilter !== 'all') {
            chips.push({
                id: 'property',
                label: `Property: ${
                    propertyOptions.find((item) => item.id === propertyFilter)
                        ?.name || 'Property'
                }`,
                onDelete: () => setPropertyFilter('all'),
            })
        }
        if (reviewFilter !== 'all') {
            chips.push({
                id: 'review',
                label:
                    reviewFilter === 'analyzed'
                        ? 'Review: Analyzed'
                        : 'Review: Pending',
                onDelete: () => setReviewFilter('all'),
            })
        }
        return chips
    }, [
        selectedTagId,
        userTags,
        onSelectedTagIdChange,
        propertyFilter,
        reviewFilter,
        propertyOptions,
    ])

    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const [previewDoc, setPreviewDoc] = useState<SampleW9 | null>(null)

    const handleView = (w9: SampleW9) => {
        setPreviewDoc(w9)
    }

    const handleDeleteClick = (w9: SampleW9) => {
        setSampleToDelete(w9)
        setDeleteDialogOpen(true)
    }

    const handleConfirmDelete = () => {
        if (!sampleToDelete) return
        deleteSampleW9.mutate(sampleToDelete.id, {
            onSuccess: () => {
                setDeleteDialogOpen(false)
                setSampleToDelete(null)
            },
        })
    }

    if (isLoadingSampleW9s) {
        return (
            <Box className={classes.loadingContainer}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography variant="h6">Sample W-9s</Typography>
                <Typography variant="body2" color="textSecondary">
                    Upload a sample once and apply it to one or more properties.
                </Typography>
            </Box>

            <DropFiles
                onDrop={(files) => {
                    if (files.length === 0) return
                    setPendingFiles(files)
                    setUploadDialogOpen(true)
                }}
                displayString={
                    isUploading
                        ? 'Uploading...'
                        : 'Drag and drop sample W-9 files here'
                }
                fileTypes={allowedFileTypes}
                multipleFiles={true}
            />

            <CatalogFilterBar
                searchPlaceholder="Search sample W-9s..."
                searchValue={searchText}
                onSearchChange={(value) => {
                    setSearchText(value)
                    setPage(0)
                }}
                onOpenFilters={() => setFilterDrawerOpen(true)}
                chips={filterChips}
                onClearAll={
                    filterChips.length > 0 ? clearDrawerFilters : undefined
                }
                resultLabel={`${filteredW9s.length} sample W-9${
                    filteredW9s.length !== 1 ? 's' : ''
                }`}
            />
            <CatalogFilterDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                title="Sample W-9 filters"
                activeCount={filterChips.length}
                onClear={clearDrawerFilters}
            >
                <PropertyTagFilterField
                    value={selectedTagId}
                    onChange={(value) => {
                        onSelectedTagIdChange(value)
                        setPage(0)
                    }}
                />
                <CatalogSelectField
                    label="Property"
                    value={propertyFilter}
                    onChange={(value) =>
                        setPropertyFilter(
                            value === 'all' ? 'all' : Number(value),
                        )
                    }
                    options={[
                        { value: 'all', label: 'All properties' },
                        ...propertyOptions.map((property) => ({
                            value: property.id,
                            label: property.name,
                        })),
                    ]}
                />
                <CatalogSelectField
                    label="AI review"
                    value={reviewFilter}
                    onChange={(value) =>
                        setReviewFilter(value as 'all' | 'analyzed' | 'pending')
                    }
                    options={[
                        { value: 'all', label: 'All reviews' },
                        { value: 'analyzed', label: 'Analyzed' },
                        { value: 'pending', label: 'Pending' },
                    ]}
                />
            </CatalogFilterDrawer>

            {/* Table */}
            <Paper className={classes.tableWrapper}>
                <TableContainer className={classes.tableContainer}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow className={classes.headerRow}>
                                <TableCell className={classes.headerCell}>
                                    Name
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    Property
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    AI Summary
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    Uploaded
                                </TableCell>
                                <TableCell
                                    className={`${classes.headerCell} ${classes.actionsCell}`}
                                    align="right"
                                >
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedW9s.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        className={classes.emptyRow}
                                    >
                                        {searchText
                                            ? 'No matching sample W-9s found'
                                            : 'No sample W-9s found'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedW9s.map((w9) => (
                                    <TableRow
                                        key={w9.id}
                                        className={classes.row}
                                        onClick={() => handleView(w9)}
                                    >
                                        <TableCell>
                                            <Box className={classes.nameCell}>
                                                <Box
                                                    className={
                                                        classes.documentIcon
                                                    }
                                                >
                                                    <DescriptionIcon fontSize="small" />
                                                </Box>
                                                <Typography variant="body2">
                                                    {w9.name}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                className={classes.propertyCell}
                                            >
                                                <span>
                                                    {propertyLabelOf(w9)}
                                                </span>
                                                {(() => {
                                                    const tags = propertyIdsOf(
                                                        w9,
                                                    ).flatMap((id) =>
                                                        getTagsForProperty(id),
                                                    )
                                                    const uniqueTags = tags.filter(
                                                        (tag, index) =>
                                                            tags.findIndex(
                                                                (other) =>
                                                                    other.id ===
                                                                    tag.id,
                                                            ) === index,
                                                    )
                                                    return uniqueTags.length >
                                                        0 ? (
                                                        <Box
                                                            display="flex"
                                                            flexWrap="wrap"
                                                            style={{ gap: 4 }}
                                                        >
                                                            {uniqueTags.map(
                                                                (tag) => (
                                                                    <Chip
                                                                        key={
                                                                            tag.id
                                                                        }
                                                                        label={
                                                                            tag.name
                                                                        }
                                                                        size="small"
                                                                        className={
                                                                            classes.tagChip
                                                                        }
                                                                        style={{
                                                                            backgroundColor:
                                                                                tag.color,
                                                                            color:
                                                                                'white',
                                                                        }}
                                                                    />
                                                                ),
                                                            )}
                                                        </Box>
                                                    ) : null
                                                })()}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {processingIds.has(w9.id) ? (
                                                <Box
                                                    className={
                                                        classes.analyzingContainer
                                                    }
                                                >
                                                    <CircularProgress
                                                        size={14}
                                                        color="primary"
                                                    />
                                                    <Typography
                                                        className={
                                                            classes.analyzingText
                                                        }
                                                    >
                                                        Analyzing document...
                                                    </Typography>
                                                </Box>
                                            ) : w9.ai_summary ? (
                                                <Tooltip
                                                    title={
                                                        <Typography
                                                            variant="body2"
                                                            style={{
                                                                whiteSpace:
                                                                    'pre-wrap',
                                                            }}
                                                        >
                                                            {w9.ai_summary}
                                                        </Typography>
                                                    }
                                                    arrow
                                                    placement="bottom-start"
                                                    interactive
                                                >
                                                    <Box
                                                        className={
                                                            classes.summaryCell
                                                        }
                                                    >
                                                        {w9.ai_summary}
                                                    </Box>
                                                </Tooltip>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {formatDate(w9.created_date)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            className={classes.actionsCell}
                                            onClick={(event) =>
                                                event.stopPropagation()
                                            }
                                        >
                                            <Tooltip title="Properties">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        setPropertiesTarget(w9)
                                                    }
                                                >
                                                    <BusinessIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Reanalyze">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            processSampleW9.mutate(
                                                                w9.id,
                                                            )
                                                        }
                                                        disabled={processingIds.has(
                                                            w9.id,
                                                        )}
                                                    >
                                                        <RefreshIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Send to Vendors">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            setSampleToSend(w9)
                                                        }
                                                        disabled={isSending}
                                                    >
                                                        <SendIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="View">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleView(w9)
                                                    }
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleDeleteClick(w9)
                                                    }
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    count={filteredW9s.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </Paper>

            <SelectPropertiesUploadDialog
                open={uploadDialogOpen}
                onClose={() => {
                    setUploadDialogOpen(false)
                    setPendingFiles([])
                }}
                files={pendingFiles}
                propertyList={propertyList}
                title="Select properties for these sample W-9s"
                submitLabel="Upload"
                isSubmitting={isUploading}
                onConfirm={async (propertyIds) => {
                    const results = await uploadSampleW9s(
                        pendingFiles,
                        propertyIds,
                    )
                    results.forEach((result) => {
                        if (
                            result.status === 'fulfilled' &&
                            result.value?.data?.id
                        ) {
                            processSampleW9.mutate(result.value.data.id)
                        }
                    })
                    setPendingFiles([])
                    setUploadDialogOpen(false)
                }}
            />

            <SelectPropertiesUploadDialog
                open={propertiesTarget != null}
                onClose={() => setPropertiesTarget(null)}
                files={[]}
                propertyList={propertyList}
                title={
                    propertiesTarget
                        ? `Properties for ${propertiesTarget.name}`
                        : 'Properties'
                }
                submitLabel="Save"
                submittingLabel="Saving..."
                initialSelectedIds={
                    propertiesTarget ? propertyIdsOf(propertiesTarget) : []
                }
                isSubmitting={isUpdating}
                onConfirm={(propertyIds) => {
                    if (!propertiesTarget) return
                    updateSampleW9.mutate(
                        {
                            id: propertiesTarget.id,
                            data: { apartment_ids: propertyIds },
                        },
                        { onSuccess: () => setPropertiesTarget(null) },
                    )
                }}
            />

            <SendSampleToVendorsDialog
                open={sampleToSend != null}
                title="Send sample W-9 to vendors"
                documentName={sampleToSend?.name || ''}
                vendors={sendVendors}
                vendorsLoading={loadingState.vendorList}
                emptyMessage="No vendors on this sample's properties"
                isSending={isSending}
                onClose={() => setSampleToSend(null)}
                onSend={(vendorIds) => {
                    if (!sampleToSend) return
                    sendToVendors.mutate(
                        { id: sampleToSend.id, vendorIds },
                        { onSuccess: () => setSampleToSend(null) },
                    )
                }}
            />

            <DeleteConfirmDialog
                isOpen={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                documentName={sampleToDelete?.name || ''}
                isDeleting={isDeleting}
            />
            <DocumentPreviewModal
                open={previewDoc != null}
                onClose={() => setPreviewDoc(null)}
                title={previewDoc?.name || 'Sample W-9'}
                fileUrl={previewDoc?.file || null}
                summary={previewDoc?.ai_summary}
            />
        </Box>
    )
}
